import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { CircularProgress } from '@material-ui/core';
import Form from 'ui/Form/Form';
import Button from 'ui/Button/Button';
import { PasswordInput } from 'ui/Input';
import routePaths from 'routes/routePaths';
import api from 'api/api';
import { validateConfirmPassword, validatePassword } from 'utils/validation';
import { mapYupObjectError, objectMap } from 'utils/helpers';
import auth from 'utils/auth';
import { IAuthData } from 'utils/models';
import Link from 'ui/Link/Link';
import { AxiosStatus } from 'api/axiosConfig';
import { StatusModal } from 'components/modals';
import { IChangePasswordResponse, IChangePasswordError, IChangePasswordFormData } from './changePassword.model';

import styles from './ChangePassword.module.css';

const schema = yup.object().shape({
  newPassword: validatePassword(),
  confirmPassword: validateConfirmPassword('newPassword'),
});

const ChangePassword = () => {
  const [success, setSuccess] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;

  const validate = useCallback((values: IChangePasswordFormData) => {
    try {
      schema.validateSync(values, { abortEarly: false });
    } catch (e) {
      const valErrs = mapYupObjectError<IChangePasswordFormData>(e);
      return objectMap(valErrs, (v) => v[0]);
    }
    return undefined;
  }, []);

  const history = useHistory();
  const handleOnSuccess = () => {
    history.push(routePaths.login);
  };

  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;
  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
  };
  const onSubmit = useCallback((values: IChangePasswordFormData) => {
    const requestBody: IChangePasswordFormData = {
      ...values,
    };
    return api
      .post('customer/changePassword', requestBody)
      .then((response: IChangePasswordResponse) => {
        setSuccess(response.description || '');
        setSubmitStatus(AxiosStatus.SUCCESS);
        auth.login({ ...(auth.data as IAuthData), usernamePasswordChangeRequired: false, firstLogin: false });
      })
      .catch((error: IChangePasswordError) => {
        setErrorMsg(error.description);
        setSubmitStatus(AxiosStatus.FAILURE);
        // Return a rejected Promise with the error message
        return Promise.reject(error.description);
      });
  }, []);

  return (
    <div className={styles.content}>
      <div className={styles.title}>Create a New Password</div>
      <p className={styles.pageDesc}>To continue, Set up your new password for corporate internet banking</p>
      <Form
        onSubmit={onSubmit}
        validate={validate}
        render={({ handleSubmit, hasValidationErrors }) => (
          <form onSubmit={handleSubmit}>
            <p className={styles.passwordDesc}>
              Should have 8 characters with at least one uppercase and one special character
            </p>
            <PasswordInput name="newPassword" label="New Password" className={styles.field} disabled={submitting} />
            <PasswordInput
              name="confirmPassword"
              label="New Password Confirmation"
              className={styles.field}
              disabled={submitting}
            />
            <Button type="submit" className={styles.submit} fullWidth disabled={submitting || hasValidationErrors}>
              {submitting ? <CircularProgress size={20} color="inherit" /> : 'Continue'}
            </Button>
            <div className={styles.policy}>
              <Link to={routePaths.privacyPolicy}>Privacy Policy</Link>
            </div>
          </form>
        )}
      />
      <StatusModal
        open={showStatusModal}
        status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
        onClose={onCloseStatusModal}
        onRetry={onCloseStatusModal}
        onRetryText="Close"
        statusText="Continue to Login"
      >
        {submitStatus === AxiosStatus.SUCCESS ? success : errorMsg}
      </StatusModal>
    </div>
  );
};

export default ChangePassword;
